import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import config from '../../../configs/config'
import { parseJsonResponse as parseApiJsonResponse } from '../../../shared/api/httpClient'

const apiUrl = config.apiurl

const getAgencyErrorMessage = (json) =>
  json?.errorMessage ||
  json?.message ||
  json?.Message ||
  'No se pudo crear la agencia'

const normalizeAgency = (agency) => {
  if (!agency) return null

  const addressParts = [
    agency.Address,
    agency.Address_2,
    agency.City,
    agency.Id_State || agency.State,
    agency.Id_Country || agency.Country,
    agency.ZipCode,
  ].filter(Boolean)

  return {
    id: agency.Id_Agency || '',
    name: agency.Name || agency.AgencyName || '',
    email: agency.ContactEmail || agency.Email || '',
    additionalEmail: agency.AdditionalEmail || '',
    address: addressParts.join(', '),
    address1: agency.Address || '',
    address2: agency.Address_2 || '',
    phone: agency.Phone_1 || agency.Phone || '',
    phone2: agency.Phone_2 || '',
    city: agency.City || '',
    state: agency.State || agency.Id_State || '',
    country: agency.Country || agency.Id_Country || '',
    zipCode: agency.ZipCode || '',
    contactName: agency.ContactName || '',
    contactLastName: agency.ContactLastName || '',
    contactTitle: agency.ContactTitle || '',
    contactEmail: agency.ContactEmail || agency.Email || '',
    agencyType: agency.Id_AgencyType || agency.AgencyType || '',
    isBranch: agency.isBranch,
    agencyHQ: agency.Id_AgencyHQ || null,
    hasHQ: agency.hasHQ,
    token: agency.Token || '',
    tacCodeId: agency.TACCodeID || '',
    isApproved: agency.isApproved,
    isMarivalRewards: agency.isMarivalRewards,
    urlName: agency.UrlName || '',
    raw: agency,
  }
}

export const searchAgencies = createAsyncThunk(
  'agency/searchAgencies',
  async (searchText = '', { rejectWithValue }) => {
    try {
      const response = await fetch(
        apiUrl + 'GetAgency?searchText=' + encodeURIComponent(searchText),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Error al consultar agencias')
      }

      const json = await parseApiJsonResponse(response)

      if (!json?.result || !Array.isArray(json.data)) {
        return []
      }

      const agencies = json.data.map((agency) => ({
        id: agency.Id_Agency,
        name: agency.AgencyName || '',
        email: agency.Email || '',
        address: [
          agency.Address,
          agency.Address_2,
          agency.City,
          agency.State,
          agency.Country,
          agency.ZipCode,
        ]
          .filter(Boolean)
          .join(', '),
        phone: agency.Phone || '',
        city: agency.City || '',
        state: agency.State || '',
        country: agency.Country || '',
        zipCode: agency.ZipCode || '',
        isBranch: agency.isBranch,
        agencyHQ: agency.Id_AgencyHQ,
        agencyType: agency.Id_AgencyType,
        contact: agency.LocationContact || '',
        raw: agency,
      }))

      const trimmed = searchText.trim().toLowerCase()

      if (!trimmed) {
        return agencies
      }

      return agencies.filter((agency) =>
        agency.name.toLowerCase().includes(trimmed)
      )
    } catch (error) {
      return rejectWithValue(error.message || 'No se pudieron cargar las agencias')
    }
  }
)

export const createAgency = createAsyncThunk(
  'agency/createAgency',
  async (payload, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams()

      Object.entries(payload || {}).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        params.append(key, String(value))
      })

      const response = await fetch(
        `${apiUrl}CreateMobileAgency?${params.toString()}`,
        {
          method: 'GET',
          headers: {
           // 'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error('Error al crear la agencia')
      }

      const json = await parseApiJsonResponse(response)

      if (!json?.result) {
        return rejectWithValue(getAgencyErrorMessage(json))
      }

      if (!json?.agency_data) {
        return rejectWithValue('La API no devolvió agency_data')
      }

      return {
        agency: normalizeAgency(json.agency_data),
        rawResponse: json,
      }
    } catch (error) {
      return rejectWithValue(error.message || 'No se pudo crear la agencia')
    }
  }
)

const agencySlice = createSlice({
  name: 'agency',
  initialState: {
    searchTerm: '',
    results: [],
    loading: false,
    creating: false,
    selectedAgency: null,
    createdAgencyResponse: null,
    error: null,
    createError: null,
  },
  reducers: {
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload
    },
    clearResults: (state) => {
      state.results = []
    },
    selectAgency: (state, action) => {
      state.selectedAgency = action.payload
      state.searchTerm = action.payload?.name || ''
      state.results = []
    },
    clearSelectedAgency: (state) => {
      state.selectedAgency = null
      state.searchTerm = ''
    },
    clearCreateAgencyState: (state) => {
      state.creating = false
      state.createError = null
      state.createdAgencyResponse = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAgencies.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchAgencies.fulfilled, (state, action) => {
        state.loading = false
        state.results = action.payload
      })
      .addCase(searchAgencies.rejected, (state, action) => {
        state.loading = false
        state.results = []
        state.error = action.payload || 'No se pudieron cargar las agencias'
      })

      .addCase(createAgency.pending, (state) => {
        state.creating = true
        state.createError = null
      })
      .addCase(createAgency.fulfilled, (state, action) => {
        state.creating = false
        state.createError = null
        state.selectedAgency = action.payload.agency
        state.searchTerm = action.payload.agency?.name || ''
        state.results = []
        state.createdAgencyResponse = action.payload.rawResponse
      })
      .addCase(createAgency.rejected, (state, action) => {
        state.creating = false
        state.createError = action.payload || 'No se pudo crear la agencia'
      })
  },
})

export const {
  setSearchTerm,
  clearResults,
  selectAgency,
  clearSelectedAgency,
  clearCreateAgencyState,
} = agencySlice.actions

export default agencySlice.reducer
